/**
 * 信件即時通知新架構的狀態與錯誤文案（SA v2.0 §2.2、C12 錯誤表）。
 *
 * 只放純資料與純函式，狀態卡、規則區、新增表單共用同一份，避免同一個錯誤在三個地方講成三種話。
 * ⛔ 文案改動要同步 SA 與 FRD_MailNotifyStatus／Rule_UI，對話工具（agent_mcp）也轉述同一段文字。
 */

/** AP 回的 state（＝APISIX subscription.status 轉大寫）→ 徽章文字 */
export const STATE_LABEL = {
  ACTIVE: '已啟用',
  DISABLED: '未啟用',
  PENDING: '處理中',
  EXPIRED: '已中斷',
  NEEDS_REAUTH: '已暫停',
  ERROR: '異常',
};

/**
 * 徽章沿用舊訂閱卡的配色（AGUI-1193）：「未啟用」比照「未訂閱」紅字不填底，
 * 「已中斷」「已暫停」「異常」比照「已失效」紅底，「處理中」比照「設定中」灰。
 */
export const STATE_CLASS = {
  ACTIVE: 'mail-alert-task__state--active',
  DISABLED: 'mail-alert-task__state--disabled',
  PENDING: 'mail-alert-task__state--pending',
  EXPIRED: 'mail-alert-task__state--failed',
  NEEDS_REAUTH: 'mail-alert-task__state--failed',
  ERROR: 'mail-alert-task__state--failed',
};

export const SYNCING_LABEL = '同步中';
export const SYNCING_SUBTITLE = '正在確認通知狀態';
export const LOAD_ERROR_SUBTITLE = '目前無法取得通知狀態。';
/** 輪詢 30 秒仍沒結果：取代「處理中，請稍候。」這一行，不另外顯示提示框（10-19 使用者裁決；文案依 AGUI-1260） */
export const POLL_TIMEOUT_SUBTITLE = '連線逾時，請稍後手動重新整理。';
export const NO_ENABLED_RULE_HINT = '請先啟用至少一條規則';

/** needs_reauth 的三種原因；收到其他值時用通用說法，按鈕照樣給「恢復通知」 */
const REAUTH_SUBTITLE = {
  CREDENTIAL_INVALID: '登入憑證已失效，通知已暫停，請按「恢復通知」',
  OUTLOOK_REAUTH_REQUIRED: 'Outlook 授權已失效，請重新連結 Outlook 後按「恢復通知」',
  OUTLOOK_ACCOUNT_CHANGED: '連結的 Outlook 帳號已變更，請確認帳號後重新連結',
};
const REAUTH_FALLBACK = '通知已暫停，請按「恢復通知」';

/** 這兩個原因要使用者先回「第三方服務」重新連結，電腦版另給「前往設定」、手機版給灰框 */
export const isOutlookReason = (reason) =>
  reason === 'OUTLOOK_REAUTH_REQUIRED' || reason === 'OUTLOOK_ACCOUNT_CHANGED';

/** 狀態轉大寫並只認得六態；其他值回 null，由呼叫端當成讀取失敗（SA §2.2：status 收到未知值） */
export const normalizeState = (state) => {
  const s = typeof state === 'string' ? state.toUpperCase() : null;
  return s && STATE_LABEL[s] ? s : null;
};

/**
 * 標題下方那一行。每個狀態都有一句，切換時版面不跳動（D-1006-01）。
 * @param {boolean} autoRebuild MyAgent 是否已完成過期自動重建（L1／L2，/web/config 的 mailNotifyAutoRebuild）
 */
export const stateSubtitle = (state, reason, autoRebuild = false) => {
  switch (state) {
    case 'ACTIVE':
      return '符合規則的新信會以 M+ 通知你';
    case 'DISABLED':
      return '新增並啟用規則後，就會開始通知';
    case 'PENDING':
      return '處理中，請稍候。';
    case 'EXPIRED':
      return autoRebuild ? '系統正在重新建立，通知可能暫時中斷' : '通知已中斷，請按「恢復通知」';
    case 'NEEDS_REAUTH':
      return REAUTH_SUBTITLE[reason] || REAUTH_FALLBACK;
    case 'ERROR':
      // AGUI-1261：異常多半是 MyAgent 端暫時建不起訂閱，恢復後頁面重讀即可看到新狀態，不必找管理員
      return '通知異常，請稍後手動重新整理。';
    default:
      return '';
  }
};

/** 要不要給「恢復通知」：已暫停一律給；已中斷只在 MyAgent 還不會自己重建時給（SA §7 S-04） */
export const canRestore = (state, autoRebuild = false) =>
  state === 'NEEDS_REAUTH' || (state === 'EXPIRED' && !autoRebuild);

/**
 * APISIX 的 operation 是否還在進行。欄位名稱以 AP 實際回傳為準，駝峰與底線都認。
 * ⛔ 狀態值 OpenAPI 只寫 type: string（NQ-11），所以只把明確寫著失敗或完成的當成結束，其餘都當進行中，寧可多等也不要提早說成功。
 */
const OPERATION_DONE = /^(succeeded|success|completed|done)$/i;
const OPERATION_FAILED = /fail|error/i;

export const operationErrorCode = (operation) =>
  operation?.lastErrorCode ?? operation?.last_error_code ?? operation?.errorCode ?? null;

export const isOperationFailed = (operation) =>
  Boolean(operation) && (OPERATION_FAILED.test(operation.status || '') || Boolean(operationErrorCode(operation)));

export const isOperationRunning = (operation) =>
  Boolean(operation) && !isOperationFailed(operation) && !OPERATION_DONE.test(operation.status || '');

/** C12 錯誤表：errorCode → 畫面文字。前端只認 errorCode，不直接顯示 APISIX 原文 */
const ERROR_TEXT = {
  VERSION_CONFLICT: '設定已在其他地方變更，請確認後再試。',
  OPERATION_IN_PROGRESS: '上一個設定還在處理，請稍候再試。',
  RULE_LIMIT_EXCEEDED: '已達 3 條規則上限，請先刪除不用的規則。',
  OUTLOOK_REAUTH_REQUIRED: 'Outlook 授權已失效，請先重新連結 Outlook。',
  // 他人的規則與已刪除的規則同一句，不讓人分辨「存在但不是我的」（FRD_UI AC-RULE-03-11）
  OWNER_SCOPE_MISMATCH: '找不到這條規則，可能已被刪除。',
  PLATFORM_NOT_ALLOWED: '通知服務異常，平台設定異常，請稍後再試。',
  RULE_NOT_FOUND: '找不到這條規則，可能已被刪除。',
  RULE_INPUT_INVALID: '規則內容不符合格式，請檢查欄位。',
  FILTER_UNSUPPORTED: '此條件組合暫不支援。',
  CREDENTIAL_INVALID: '登入憑證已失效，請重新登入 MyBro 後再試。',
  GRAPH_SUBSCRIPTION_INVALID_REQUEST: '通知服務異常，訂閱設定異常，請稍後再試。',
  WEBHOOK_DNS_UNRESOLVED: '通知服務異常，訂閱設定異常，請稍後再試。',
  NO_ENABLED_RULE: NO_ENABLED_RULE_HINT,
};
const SERVICE_UNAVAILABLE = '通知服務暫時無法使用，請稍後再試。';

/** 各動作在沒有對應文案時的說法（沿用 v1.7） */
export const FALLBACK = {
  save: '儲存失敗，請稍後再試。',
  toggle: '操作失敗，請稍後再試。',
  delete: '刪除失敗，請稍後再試。',
  setting: '設定失敗，請稍後再試。',
};

/**
 * 把錯誤轉成畫面要顯示的 `{ message, errorCode }`。
 * `errorCode` 留著給 {@link MailNotifyErrorNotice} 判斷要不要附「前往設定」。
 */
export const describeError = (error, fallback) => {
  const errorCode = error?.errorCode || null;
  if (errorCode && ERROR_TEXT[errorCode]) return { message: ERROR_TEXT[errorCode], errorCode };
  if (error?.code === 503) return { message: SERVICE_UNAVAILABLE, errorCode };
  // 沒有 errorCode 的 404：規則已不在（AP 對應不到也一樣處理）
  if (error?.code === 404) return { message: ERROR_TEXT.RULE_NOT_FOUND, errorCode: 'RULE_NOT_FOUND' };
  return { message: fallback, errorCode };
};

/**
 * 讀取狀態失敗時的說明行（FUNC-037）：通知服務暫時無法使用（503）講清楚是服務端的問題，
 * 其餘一律「目前無法取得通知狀態。」——⛔ 403 OWNER_SCOPE_MISMATCH 也是這句，不透露是權限問題（SEC-003）。
 */
export const loadErrorText = (error) => (error?.code === 503 ? SERVICE_UNAVAILABLE : LOAD_ERROR_SUBTITLE);

/** 版本衝突後重讀也失敗：手上的 rulesVersion 還是舊的，重按只會再回 409，請使用者重新整理（10-21） */
export const STALE_VERSION_TEXT = '目前無法取得最新設定，請重新整理後再試。';

/** 上一次操作失敗、但沒有對應文案的錯誤碼（例如 GRAPH_THROTTLED）時的說法（FUNC-035） */
export const LAST_OPERATION_FALLBACK = '上一次設定沒有完成，請再試一次。';

/**
 * 讀回來的 operation 是失敗時，狀態卡下方要顯示的紅色錯誤（FRD AC-NTF-01-7，FUNC-030～035）。
 * 不限於這個分頁送出的操作：在別的分頁、對話或 MyAgent 改壞的，進頁面也要看得到。
 * `reportedErrorCode` 是這個頁面最近一次寫入已在動作現場報過的錯誤碼，同一碼不再顯示，避免同一句話出現兩次。
 */
export const lastOperationError = (operation, reportedErrorCode = null) => {
  if (!isOperationFailed(operation)) return null;
  const errorCode = operationErrorCode(operation);
  if (reportedErrorCode && errorCode === reportedErrorCode) return null;
  return describeError({ errorCode }, LAST_OPERATION_FALLBACK);
};

/** 這些錯誤代表畫面上的資料已經舊了，要重讀清單與狀態（情境 K、C11 404） */
export const needsReload = (error) =>
  ['VERSION_CONFLICT', 'RULE_LIMIT_EXCEEDED', 'RULE_NOT_FOUND', 'OWNER_SCOPE_MISMATCH', 'OPERATION_IN_PROGRESS'].includes(error?.errorCode) ||
  error?.code === 404;
